/* eslint-disable react-hooks/exhaustive-deps */
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { Formik, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Offcanvas } from "react-bootstrap";
import * as Yup from "yup";

import {
  RISelfAddressInfoForm,
  RISelfNetTaxableIncomeForm,
  RISelfOwnerDetailsForm,
  RISelfTaxSavingDeductionForm
} from "..";
import { PropertTypes } from "../../../../config/filingConstants";
import { IFormITR1, ISelfOccupiedProperties } from "../../../../config/interfaces";
import {
  AddressFormInitValues,
  SelfOccupiedPropertiesFormSchema
} from "../../../../pages/itr-filing/efiling.config";
import { useAppSelector } from "../../../../redux/Store";
import { ITRFilingSelector } from "../../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../../FASvgIcon";
import ScrollToFirstError from "../../../ScrollToFirstError";

type Props = {
  showCanvas: boolean;
  formValues: ISelfOccupiedProperties | null;
  handleCloseBtnPress: (salaryData: ISelfOccupiedProperties | null) => void;
};

const RISelfOccupiedPropertiesDetailsForm: React.FC<Props> = (props: Props) => {
  const { showCanvas, handleCloseBtnPress, formValues } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const ITR1 = useFormikContext<IFormITR1>();

  const fullname = `${ITR1.values?.FirstName || ""} ${ITR1.values?.MiddleName || ""} ${
    ITR1.values?.SurNameOrOrgName || ""
  }`;

  const FormInitValues: ISelfOccupiedProperties = {
    PropertyType: PropertTypes.SELF,
    IsSameAddress: false,
    Address: { ...AddressFormInitValues },
    OwnerDetails: [
      {
        FullName: fullname,
        Pan: `${ITR1.values.PAN}`,
        Percentage: "100"
      }
    ],
    TotalAnnualRentReceived: "",
    MunicipalTax: "",
    NetIncome: "",
    StandardDeductionUs24A: "",
    FinancialYear: "",
    InterestPreConstruction: "",
    InterestPostConstruction: "",
    TotalDeduction: "",
    NetTaxableIncome: ""
  };

  const [initFormValues, setInitFormValues] = useState<ISelfOccupiedProperties>(FormInitValues);

  useEffect(() => {
    const selfProperties =
      ITR1.values?.IncomeDetails?.RISelfOccupiedProperties?.map(
        (item) => item.PropertyType === PropertTypes.SELF
      ) || [];

    if (showCanvas && formValues) {
      setInitFormValues(() => cloneDeep(formValues));
    } else {
      setInitFormValues(() => ({
        ...cloneDeep(FormInitValues),
        PropertyType: selfProperties.length >= 2 ? PropertTypes.DEEMED : PropertTypes.SELF
      }));
    }
  }, [showCanvas, formValues]);

  return (
    <>
      <Offcanvas show={showCanvas} placement="end" backdrop={true}>
        <Formik
          enableReinitialize={true}
          initialValues={initFormValues}
          validationSchema={Yup.object().shape({ ...SelfOccupiedPropertiesFormSchema })}
          onSubmit={(values) => handleCloseBtnPress(values)}
        >
          {({ handleSubmit, submitCount, isValid, errors, values }) => (
            <>
              <ScrollToFirstError submitCount={submitCount} isValid={isValid} errors={errors} />

              <Offcanvas.Header>
                <Offcanvas.Title>Self Occupied Properties (not given on rent)</Offcanvas.Title>
                <Button
                  variant="primary"
                  className="icon-only hide-offcanvas"
                  onClick={() => handleCloseBtnPress(null)}
                >
                  <FASvgIcon icon={faXmark} />
                </Button>
              </Offcanvas.Header>

              <Offcanvas.Body>
                <RISelfAddressInfoForm addressValues={ITR1.values.Address} />
                <RISelfOwnerDetailsForm />
                <RISelfTaxSavingDeductionForm />
                <RISelfNetTaxableIncomeForm />
              </Offcanvas.Body>

              <div className="offcanvas-btns">
                {!isFilingReadOnly ? (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => {
                      let sum = values.OwnerDetails.slice(1).reduce(
                        (acc, item) => acc + (parseInt(item.Percentage) || 0),
                        0
                      );
                      const firstOwnerPercentage = 100 - sum;
                      if (sum > 100 || firstOwnerPercentage === 0) return;
                      handleSubmit();
                    }}
                  >
                    Save and Close
                  </Button>
                ) : (
                  <Button
                    variant="outline-white"
                    size="sm"
                    onClick={() => handleCloseBtnPress(null)}
                  >
                    Close
                  </Button>
                )}
              </div>
            </>
          )}
        </Formik>
      </Offcanvas>
    </>
  );
};

export default RISelfOccupiedPropertiesDetailsForm;
